import { DateTime } from 'luxon'
import { processAmounts } from '@/helpers/results-helper'
import {
  timeObjectFromSelectedTimePeriod,
  getTimePeriodSinceSurveyEndDate,
  surveyIsWithinTimePeriod
} from '@/helpers/time-window-helper'

export const simpleLinearRegression = ({ averages, roundToDecimals = 3 }) => {
  const cleanedAverages = averages.filter((a) => a.value !== undefined)
  // Convert timestamps to days since the first timestamp
  if (!cleanedAverages?.length) {
    return []
  }

  const dates = cleanedAverages.map((a) => new Date(a.date_modified))

  // Get the first date (in milliseconds)
  const firstDate = dates[0].getTime()

  // Map the date array to get the difference in days
  const timestamps = dates.map((date) =>
    Math.round((date.getTime() - firstDate) / (24 * 60 * 60 * 1000))
  )

  const values = cleanedAverages.map((item) =>
    item.npsData?.score ? item.npsData.score : item.value
  )

  // Calculate the means of timestamps and values
  const meanTimestamp =
    timestamps.reduce((a, b) => a + b, 0) / timestamps.length
  const meanValue = values.reduce((a, b) => a + b, 0) / values.length

  // Calculate the slope (m) and intercept (b) for y = mx + b
  let numerator = 0
  let denominator = 0
  for (let i = 0; i < timestamps.length; i++) {
    numerator += (timestamps[i] - meanTimestamp) * (values[i] - meanValue)
    denominator += (timestamps[i] - meanTimestamp) ** 2
  }
  const slope = numerator / denominator
  const intercept = meanValue - slope * meanTimestamp

  // Calculate the line of best fit values
  return timestamps.map((timestamp) => {
    const value = slope * timestamp + intercept
    return parseFloat(value.toFixed(roundToDecimals))
  })
}

const valueForItem = ({ statItem }) => {
  return statItem.npsData ? statItem.npsData.score : statItem.value
}

export const datesForSurveys = ({ surveyDetails }) => {
  const currentSurvey = {
    details: surveyDetails.details,
    name: surveyDetails.name
  }

  return surveyDetails.oldSurveysInfo
    .concat(currentSurvey)
    .reduce((res, survey) => {
      let start_date = survey.details.start_date
      let end_date = survey.details.end_date
      if (!!survey.details.aggregate) {
        // instead check aggregate time window
        const t = survey.details.aggregate.config.timeWindow
        end_date = t.from_date
        start_date = DateTime.fromISO(t.from_date)
          .minus({ [t.unit]: t.amount })
          .toISO()
        if (t.from_date) {
        } else {
          // use today as starting point, looking backwards
          const today = DateTime.now().endOf('day')
          end_date = today.toISO()
          start_date = today.minus({ [t.unit]: t.amount }).toISO()
        }
      }
      res[survey.name] = {
        start_date,
        end_date
      }
      return res
    }, {})
}

const aggregateIsWithinHistoryPeriod = ({
  surveyDetails,
  timePeriodStartDate,
  aggregateToCompareWith
}) => {
  const timePeriodEndDate = DateTime.fromISO(
    surveyDetails.details.aggregate.config.timeWindow.from_date ||
      DateTime.local().endOf('day').toISO()
  )
  const w = aggregateToCompareWith.details.aggregate.config.timeWindow
  const surveyEndDate = DateTime.fromISO(w.from_date)
  return (
    surveyEndDate >= timePeriodStartDate && surveyEndDate <= timePeriodEndDate
  )
}

export const oldSurveysWithinTimePeriod = ({
  surveyDetails,
  selectedTimePeriod
}) => {
  const timePeriod = timeObjectFromSelectedTimePeriod({ selectedTimePeriod })
  const isAggregate = !!surveyDetails.details.aggregate
  const timePeriodStartDate = getTimePeriodSinceSurveyEndDate({
    surveyDetails,
    timePeriod
  })
  if (isAggregate) {
    // const surveyStartDate = DateTime.fromISO(surveyDetails.details.start_date)
    // const surveyEndDate = DateTime.fromISO(surveyDetails.details.end_date)
    const oldAggregates = surveyDetails.oldSurveysInfo.filter(
      (s) => !!s.details.aggregate
    )
    return oldAggregates
      .filter((s) => {
        return aggregateIsWithinHistoryPeriod({
          timePeriodStartDate,
          surveyDetails,
          aggregateToCompareWith: s
        })
      })
      .map((s) => s.name)
  } else {
    return surveyDetails.oldSurveysInfo
      .filter((s) => {
        return surveyIsWithinTimePeriod({
          timePeriodStartDate,
          surveyToCompareWith: s,
          surveyDetails
        })
      })
      .map((s) => s.name)
  }
}

export const trendsForTimePeriod = ({
  trends,
  surveyName,
  surveyDetails,
  selectedTimePeriod
}) => {
  // reduce trends to only include averages that are within the selected time period
  // trends is an object with keys for tags, categories, and questions
  // each of these keys has an object with keys for each tag, category, or question

  const timePeriod = timeObjectFromSelectedTimePeriod({ selectedTimePeriod })
  const datesAndSurveys = datesForSurveys({ surveyDetails })
  // datesAndSurveys[surveyName] =  // TODO add current survey

  const filteredTrends = Object.keys(trends).reduce(
    (filteredStats, trendType) => {
      filteredStats[trendType] = Object.keys(trends[trendType]).reduce(
        (filteredItems, itemName) => {
          const item = trends[trendType][itemName]
          const averagesForItem = item.averages

          if (!averagesForItem.length) {
            filteredItems[itemName] = { ...item, averages: [] }
            return filteredItems
          }

          // Filter averages based on the time period
          const filteredAverages = averagesForItem
            .filter((a) => {
              const oldSurvey = datesAndSurveys[a.surveyName]
              const timePeriodStartDate = getTimePeriodSinceSurveyEndDate({
                surveyDetails,
                timePeriod
              })
              return (
                Object.keys(a).includes('value') &&
                a.value !== undefined &&
                (a.surveyName === surveyName ||
                  (oldSurvey &&
                    surveyIsWithinTimePeriod({
                      surveyDetails,
                      surveyToCompareWith: { details: oldSurvey },
                      timePeriodStartDate
                      // itemName,
                      // debug: true
                    })))
              )
            })
            .map((a) => {
              const oldSurvey = datesAndSurveys[a.surveyName]
              return {
                ...a,
                date_modified: oldSurvey.end_date
              }
            })
            .sort((a, b) => {
              return new Date(a.date_modified) - new Date(b.date_modified)
            })

          // we don't keep any averages if only current survey is available
          const averages = filteredAverages.length === 1 ? [] : filteredAverages
          if (averages.length) {
            filteredItems[itemName] = {
              ...item,
              averages,
              regressionValues: simpleLinearRegression({
                averages: filteredAverages
              })
            }
          }
          return filteredItems
        },
        {}
      )
      return filteredStats
    },
    {}
  )
  return filteredTrends
}

export const trendComputer = ({
  allResults,
  surveyName,
  surveyDetails,
  segmentName
}) => {
  const historicalSurveyResults = Object.keys(allResults)
    .filter((name) => {
      const s = allResults[name]
      // TODO: find excludeOrganizationStatistics for survey
      return (
        name !== surveyName && !s.details?.config?.excludeOrganizationStatistics
      )
    })
    .reduce((res, name) => {
      res[name] = allResults[name]
      return res
    }, {})

  const dates = datesForSurveys({
    surveyDetails
  })

  const stats = Object.keys(allResults[surveyName])
    .filter((type) => ['categories', 'tags', 'questions'].includes(type))
    .reduce(
      (stats, type) => {
        const statsForType = allResults[surveyName][type]

        Object.keys(statsForType).forEach((statItemName) => {
          const statItem = statsForType[statItemName]
          const answersForThisSurvey = []
          processAmounts(
            answersForThisSurvey,
            statItem,
            (target, i, amount) => {
              target.push({ value: i, amount: amount })
            }
          )
          const currentSurveyResults = {
            answers: answersForThisSurvey,
            sum: statItem.sum,
            amount: statItem.amount,
            surveyName,
            value: valueForItem({ statItem }),
            date_modified: statItem.date_modified
          }

          if (statItem.npsData) {
            currentSurveyResults.npsData = statItem.npsData
          }
          const averages = [currentSurveyResults]
            .concat(
              Object.keys(historicalSurveyResults)
                .filter((surveyName) => {
                  return historicalSurveyResults[surveyName]?.[type]?.[
                    statItemName
                  ]
                })
                .map((surveyName) => {
                  const statItem =
                    historicalSurveyResults[surveyName][type][statItemName]
                  const answersForHistoricalSurvey = []
                  processAmounts(
                    answersForHistoricalSurvey,
                    statItem,
                    (target, i, amount) => {
                      target.push({ value: i, amount })
                    }
                  )
                  const a = {
                    answers: answersForHistoricalSurvey,
                    sum: statItem.sum,
                    amount: statItem.amount,
                    surveyName,
                    date_modified: dates[statItem.surveyName].end_date,
                    value: valueForItem({ statItem })
                  }

                  if (statItem.npsData) {
                    a.npsData = statItem.npsData
                  }

                  return a
                })
            )
            .sort((a, b) => {
              // oldest first
              return new Date(a.date_modified) - new Date(b.date_modified)
            })

          stats[type][statItemName] = {
            averages,
            regressionValues: simpleLinearRegression({
              averages
            })
          }
        })

        return stats
      },
      { tags: {}, categories: {}, questions: {} }
    )
  if (allResults[surveyName].userAttributes) {
    const userAttributes = Object.keys(
      allResults[surveyName].userAttributes
    ).reduce((attributes, attributeName) => {
      Object.keys(
        (attributes[attributeName] =
          allResults[surveyName].userAttributes[attributeName])
      ).reduce((segments, segmentName) => {
        const segmentResults =
          allResults[surveyName].userAttributes[attributeName][segmentName]
        segments[segmentName] = trendComputer({
          allResults: { [surveyName]: segmentResults },
          surveyName,
          surveyDetails,
          segmentName
        })
        return segments
      }, {})
      return attributes
    }, {})
    return { ...stats, userAttributes }
  } else {
    return stats
  }
}
